import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Typography, notification, Modal } from 'antd'
import React, { useState, type JSX } from 'react'

import { SettingsCard, SettingsRow, SettingsRows } from '../../../Settings'

import { SubscriptionV2, deleteSubscription, postBillingPaywall } from 'api/billing'
import { putClient } from 'api/clients'
import { QUERIES } from 'constants/queries'
import { Switch } from 'shared/components/Switch'

const shouldShowCancelSubscription = (subscription?: SubscriptionV2): boolean => {
  const { billingSubscriptionId, billingProvider } = subscription || {}
  // We need a subscription id to do the request
  if (!billingSubscriptionId) return false
  // We dont want to use this endpoint for recurly subscriptions
  // We should use the reculry portal or let them go through the dunning cycle
  if (billingProvider === 'recurly') return false
  return true
}

export function Subscription({ client, subscription }: { client: any; subscription?: SubscriptionV2 }): JSX.Element {
  const queryClient = useQueryClient()
  const recurlyAccountLink = `https://${import.meta.env.VITE_RECURLY_ACCOUNT_BASE_URL}/accounts/${client.id}`
  const [showEnforcePaywallModal, setShowEnforcePaywallModal] = useState(false)
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false)
  const { mutate: paywallClient } = useMutation(postBillingPaywall, {
    onError() {
      notification.error({
        message: 'Could not paywall client. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
    onSuccess() {
      notification.success({
        message: 'Success',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
    },
  })

  const { mutate: cancelSubscription } = useMutation(deleteSubscription, {
    onError() {
      notification.error({
        message: 'Could not cancel subscription. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
    onSuccess() {
      notification.success({
        message: 'Subscription cancelled',
        placement: 'bottomRight',
        duration: 2,
      })

      queryClient.setQueryData([QUERIES.BILLING.SUBSCRIPTION, { clientId: client.id }], undefined)
      queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
      queryClient.invalidateQueries([QUERIES.BILLING.CLIENT, { clientId: client.id }])
    },
  })

  const { billingProvider, state, billingSubscriptionId } = subscription || {}
  const { planName, subtotal, collectionMethod, planId, description, planCode } = subscription?.subscription || {}

  return (
    <>
      <SettingsCard title="Billing State">
        <SettingsRows>
          <SettingsRow description="Required before the paywall can be enforced" label="Billing Required">
            <Switch
              defaultChecked={client.onboardingBillingState.billingRequired}
              name="Billing Required"
              onSuccess={() => queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])}
              prepare={(value) => ({
                id: client.id,
                body: { billing_required: value },
              })}
              updateEntity={putClient}
            />
          </SettingsRow>
          <SettingsRow description="Whether the client added billing details" label="Billing Provided">
            {client.onboardingBillingState.billingProvided ? 'Yes' : 'No'}
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>
      <SettingsCard title="Subscription">
        <SettingsRows>
          {billingProvider === 'recurly' && (
            <>
              <SettingsRow label="Billing Provider">{billingProvider}</SettingsRow>
              <SettingsRow label="Plan">{planName}</SettingsRow>
              <SettingsRow label="Recurly Account">
                <a href={recurlyAccountLink} rel="noreferrer noopener" target="_blank">
                  {recurlyAccountLink}
                </a>
              </SettingsRow>
              <SettingsRow label="Amount">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(subtotal || 0)}
              </SettingsRow>
              <SettingsRow label="Billing">
                <Typography.Text code>{collectionMethod}</Typography.Text>
              </SettingsRow>
              <SettingsRow label="Recurly ID">
                <Typography.Text code copyable>
                  {planId}
                </Typography.Text>
              </SettingsRow>
              <SettingsRow label="Billing State">
                <Typography.Text code>{state}</Typography.Text>
              </SettingsRow>
            </>
          )}
          {billingProvider === 'stripe' && (
            <>
              <SettingsRow label="Billing Provider">{billingProvider}</SettingsRow>
              <SettingsRow label="Plan">{planName}</SettingsRow>
              <SettingsRow label="Description">{description}</SettingsRow>
              <SettingsRow label="Plan Code">{planCode}</SettingsRow>
            </>
          )}
          {billingProvider === null && (
            <>
              <SettingsRow label="Plan">{planName}</SettingsRow>
              <SettingsRow label="Description">{description}</SettingsRow>
              <SettingsRow label="Plan Code">{planCode}</SettingsRow>
            </>
          )}
          <SettingsRow
            description="Locks the leader out of the app until they add a credit card"
            label="Enforce Paywall"
          >
            <Button
              aria-label="Enforce paywall"
              disabled={!client.onboardingBillingState.billingRequired}
              onClick={() => setShowEnforcePaywallModal(true)}
            >
              Enforce Paywall
            </Button>
          </SettingsRow>
          {shouldShowCancelSubscription(subscription) && (
            <SettingsRow
              description="Removes the subscription and sets the account to inactive"
              label="Cancel Subscription"
            >
              <Button aria-label="Cancel subscription" danger onClick={() => setShowCancelSubscriptionModal(true)}>
                Cancel Subscription
              </Button>
            </SettingsRow>
          )}
        </SettingsRows>
      </SettingsCard>

      <Modal
        onCancel={() => setShowEnforcePaywallModal(false)}
        onOk={() => {
          paywallClient({
            clientId: client.id,
          })
          setShowEnforcePaywallModal(false)
        }}
        open={showEnforcePaywallModal}
        title="Enforce Paywall"
      >
        <p>This action will lock the leader out of the app until they add a credit card.</p>
      </Modal>
      <Modal
        onCancel={() => setShowCancelSubscriptionModal(false)}
        onOk={() => {
          cancelSubscription({
            clientId: client.id,
            billingSubscriptionId: billingSubscriptionId!,
          })
          setShowCancelSubscriptionModal(false)
        }}
        open={showCancelSubscriptionModal}
        title="Cancel Subscription"
      >
        <p>
          This action will remove the leaders subscription, set the account to inactive and set the billing provided to
          false.
        </p>
      </Modal>
    </>
  )
}
